import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { RequestLogRow } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Input } from '@/app/components/ui/Field';
import { Button } from '@/app/components/ui/Button';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';

/**
 * System › API requests (`GET /requests`, § Beheer): the ingest API's
 * request log for one `source_id`, optionally narrowed to a time window. In
 * the style of Audit — filters go to the server, this page shows what comes
 * back. `RequestLogRow` (`src/api/types.ts`) was reserved by WP-06 for this
 * page.
 *
 * `source_id` is required by the API (`400` without it) — there is no
 * discovery endpoint for known source ids, so the filter starts empty and the
 * table only loads once a value is typed in, same shape as Audit's `action`/
 * `scope_id` filters.
 *
 * `GET /requests` was, until WP-03's API change, reachable only with the
 * Keycloak client role `nolatr-admin` — platform diagnostics, not a
 * dashboard-admin operation. WP-03 added `requests.view` to the `dashboard`
 * client's role set so a plain admin grant unlocks it here too (see
 * `permissions.canViewApiRequests`); this page assumes that change is live on
 * the API it talks to.
 */
export function RequestsPage() {
  const { t } = useTranslation();
  const [sourceId, setSourceId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState<{ source_id: string; from: string; to: string } | null>(
    null,
  );

  const rows = useAsync(async () => {
    if (applied === null || applied.source_id === '') return [];
    return apiGet('/requests', {
      query: {
        source_id: applied.source_id,
        ...(applied.from !== ''
          ? { start_time_unix: Math.floor(Date.parse(applied.from) / 1000) }
          : {}),
        ...(applied.to !== '' ? { end_time_unix: Math.floor(Date.parse(applied.to) / 1000) } : {}),
      },
    }) as unknown as Promise<RequestLogRow[]>;
  }, [applied]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('manage.requests.title')} lead={t('manage.requests.lead')} />

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ source_id: sourceId, from, to });
        }}
      >
        <Field label={t('manage.requests.filterSource')} className="w-48">
          <Input
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            placeholder={t('manage.requests.sourcePlaceholder')}
            required
          />
        </Field>
        <Field label={t('manage.requests.filterFrom')} className="w-56">
          <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('manage.requests.filterTo')} className="w-56">
          <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Button type="submit" disabled={sourceId.trim() === ''}>
          {t('manage.requests.filter')}
        </Button>
      </form>

      {applied === null ? (
        <EmptyState text={t('manage.requests.enterSourceHint')} />
      ) : (
        <AsyncSection state={rows} label={t('manage.requests.rowsLabel')} skeleton="table">
          {(data) =>
            data.length === 0 ? (
              <EmptyState text={t('manage.requests.empty')} />
            ) : (
              <DataTable
                rowKey={(row) => row.id}
                rows={data}
                columns={[
                  {
                    key: 'when',
                    header: t('manage.requests.when'),
                    render: (row) => row.created_at,
                  },
                  {
                    key: 'method',
                    header: t('manage.requests.method'),
                    render: (row) => row.method,
                  },
                  { key: 'path', header: t('manage.requests.path'), render: (row) => row.path },
                  {
                    key: 'status',
                    header: t('manage.requests.status'),
                    render: (row) => row.status,
                  },
                ]}
              />
            )
          }
        </AsyncSection>
      )}
    </section>
  );
}
